import Api, { usersEndpoints } from "../api";
import { login, logout, refreshImpersonation } from "../redux/reducers/session/actions";
import type { PersistedRootState, RootState } from "../redux/reducers/session/state";
import isTokenExpiredOrInvalid from "./isTokenExpiredOrInvalid";

export interface RefreshMeParams {
  state: PersistedRootState | RootState;
  dispatch: (action: { type: string; payload?: unknown }) => void;
}

// Mutex to prevent concurrent refreshMe calls during HMR
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

const refreshMe = async ({ state, dispatch }: RefreshMeParams): Promise<void> => {
  // Prevent concurrent refreshMe calls (race condition during HMR)
  if (isRefreshing) {
    await refreshPromise;
    return;
  }

  isRefreshing = true;
  refreshPromise = doRefreshMe({ state, dispatch }).finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
};

const doRefreshMe = async ({ state, dispatch }: RefreshMeParams): Promise<void> => {
  const sessionState = state.sessionState || {};
  const { token, user, adminToken, impersonatedUserId } = sessionState;

  try {
    // If state isn't rehydrated yet, don't make decisions - wait for next refresh
    if ("_persist" in sessionState && sessionState._persist && !sessionState._persist.rehydrated) {
      return;
    }

    if (!user || !token) {
      return dispatch(logout());
    }

    // If in impersonation mode, validate adminToken
    if (adminToken && impersonatedUserId) {

      // Temporarily switch to admin token to validate it
      const currentToken = Api.token;
      Api.token = adminToken;

      try {
        // Validate admin token is still valid
        const { data: adminUser } = await usersEndpoints.getMe();

        if (!adminUser || adminUser.role?.name !== "Admin") {
          // Admin token is invalid, force logout
          console.warn("Admin token is invalid during impersonation, forcing logout");
          Api.token = currentToken;
          return dispatch(logout());
        }

        // Admin token is valid, restore impersonated token and continue
        Api.token = currentToken;
      } catch (adminTokenError: unknown) {
        // Only logout if the token is explicitly expired/invalid
        if (isTokenExpiredOrInvalid(adminTokenError)) {
          console.warn("Admin token expired/invalid during impersonation:", adminTokenError);
          Api.token = currentToken;
          return dispatch(logout());
        }
        // For server/network errors or permission issues, restore token and continue with cached user data
        console.warn("Admin token validation failed (server/network error), keeping session:", adminTokenError);
        Api.token = currentToken;
        dispatch(login(token, user));
        return;
      }
    }

    // Validate current user token (either admin or impersonated user)
    dispatch(login(token, user));

    const { data: updatedUser } = await usersEndpoints.getMe(); // Remember, role has to have "/me" permission
    const roleName = updatedUser?.role?.name ?? "";

    if (["Admin", "Authenticated", "Not verified"].includes(roleName)) {
      if (adminToken && impersonatedUserId) {
        dispatch(refreshImpersonation(updatedUser));
      } else {
        dispatch(login(token, updatedUser));
      }
    } else {
      // Unknown role - don't logout, just log a warning and keep existing session
      console.warn("refreshMe: Unexpected role:", roleName, "- keeping existing session");
      dispatch(login(token, user));
    }
  } catch (e: unknown) {
    console.error("Error in refreshMe:", e);
    // Only logout if the token is explicitly expired or invalid
    // For server errors, network errors, or permission issues, keep the user logged in
    if (isTokenExpiredOrInvalid(e)) {
      console.warn("Token expired/invalid, logging out user");
      dispatch(logout());
    } else {
      console.warn("Server/network error, keeping user logged in with cached data");
      // Keep the user logged in with the existing session data
      if (user) dispatch(login(token ?? "", user));
    }
  }
};

export default refreshMe;
